/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {FormEvent, MouseEvent, useCallback, useMemo, useState} from 'react';
import isEmpty from 'lodash.isempty';
import {
  getAsset,
  updateDevice
} from '@ion/core/src/services/Connect';
import {
  entityId
} from '@ion/core/src/services/ConnectUtils';
import {Device} from '@ion/core/src/types/entities';
import {isValidEngineReference, isValidOdometerReference} from '@ion/core/src/utils/AssetUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {AssetFormFields} from './AssetEditor';
import {serialize} from './AssetEditorUtils';

// defines the params definition of the useAssetSave hook...
interface AssetSave {
  formState: object;
  onSubmit?: Function;
  handleAudit?: Function;
  removedDevice?: Object;
  onMoveDevice?: Function;
  setFormState?: Function;
  addedDevicesRef?: Object;
  isMultiSelectDevice?: boolean;
  isEditMode?: boolean;
  movingToGroup?: Object;
  isDeviceRemoved?: boolean;
  includeAssetAssociation?: boolean;
  isDeviceAssociated?: boolean;
  defaultDevice: Device[];
}

/**
 * Defining the useAssetSave generic hook which can be utilized
 * AssetEditor to save the Asset Form.
 *
 * ```typescript
 *
 * import useAssetSave from './useAssetSave';
 *
 * const {onSave} = useAssetSave({
 *   onSubmit,
 *   formState,
 *   handleAudit,
 *   removedDevice,
 *   onMoveDevice,
 *   setFormState,
 *   addedDevicesRef,
 *   isMultiSelectDevice,
 *   isEditMode,
 *   movingToGroup,
 *   isDeviceRemoved,
 *   includeAssetAssociation,
 *   isDeviceAssociated,
 *   defaultDevice
 * });
 *
 * ```
 */
export const useAssetSave = ({
  onSubmit,
  formState,
  handleAudit,
  removedDevice,
  onMoveDevice,
  setFormState,
  addedDevicesRef,
  isMultiSelectDevice,
  isEditMode,
  movingToGroup,
  isDeviceRemoved,
  includeAssetAssociation,
  isDeviceAssociated,
  defaultDevice
}: AssetSave) => {
  const [errorText, setErrorText] = useState<string>(null);
  const [isDeviceAssociating, setIsDeviceAssociating] = useState(false);

  const validator = useMemo(() => ({
    [AssetFormFields.EngineHoursReference] : ({value}) => (
      value && !isValidEngineReference(value)
    ),
    [AssetFormFields.OdometerReference] : ({value}) => (
      value && !isValidOdometerReference(value)
    ),
    undefined : ({value, required}) => (
      required && !value
    )
  }), []);

  const clearErrors = useCallback(() => {
    setErrorText(null);
  }, []);

  const onSave = useCallback(async(e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>, options) => {
    e.preventDefault();
    setErrorText(null);
    const errors = {};
    for (const [key, val] of Object.entries(formState)) {
      const {value, required, error} = val;
      if (error || validator[key]?.(val) || (required && !value)) {
        errors[key] = {
          name  : key,
          error : true,
          required
        };
      }
    }
    if (isObjectEmpty(errors)) {
      try {
        setIsDeviceAssociating(true);
        const assetData = serialize(formState);
        const validRemovedDevices = removedDevice?.filter(
          ({device}) => !addedDevicesRef.current?.find(({device: addedDevice}) => addedDevice?.id === device?.id)
        );
        addedDevicesRef.current = addedDevicesRef.current?.filter(
          ({device}) => assetData?.devices?.find(({id}) => id === device?.id)
        );
        const modifiedDevices = validRemovedDevices
          ? addedDevicesRef.current.concat(validRemovedDevices)
          : addedDevicesRef.current;
        const {href: assetAccountHref} = assetData?.account || {};
        const {href: movingToGroupHref} = movingToGroup || {};
        if (isMultiSelectDevice) {
          let isDeviceMoveValid = false;
          for (const apiCall of modifiedDevices) {
            const {device, group, isRemoved} = apiCall;
            const {href: groupHref} = group || {};
            isDeviceMoveValid = isRemoved || (!isEmpty(group) && entityId(groupHref) !== entityId(assetAccountHref));
            if (isDeviceMoveValid) {
              const deviceDetails = {
                ...device,
                account : {
                  href : isRemoved ? groupHref : assetAccountHref,
                  rel  : 'account'
                },
                asset : null
              };
              await updateDevice(deviceDetails);
            }
          }
          if (isDeviceMoveValid && isEditMode) {
            const refreshedAsset = await getAsset(assetData.id);
            assetData.version = refreshedAsset?.version;
          }
        } else if (
          !isMultiSelectDevice &&
        !isEmpty(movingToGroup) &&
        entityId(movingToGroupHref) !== entityId(assetAccountHref)
        ) {
          const device = {
            ...removedDevice,
            account : {
              href : isDeviceRemoved ? movingToGroupHref : assetAccountHref,
              rel  : 'account'
            },
            asset : null
          };
          await updateDevice(device);
          onMoveDevice(movingToGroup);
          if (isEditMode && isDeviceRemoved) {
            const refreshedAsset = await getAsset(assetData.id);
            assetData.version = refreshedAsset?.version;
          }
        }
        if (includeAssetAssociation || isMultiSelectDevice) {
          const {devices} = assetData;
          if (devices) {
            assetData.devices = devices.map(({_self}) => _self);
          }
        }
        if (isDeviceAssociated) {
          assetData.devices = defaultDevice;
          const {device} = assetData;
          if (device) {
            assetData.device = device.map(({_self}) => _self);
          }
        }
        if (isMultiSelectDevice && assetData?.device) {
          delete assetData.device;
        }
        onSubmit?.(assetData);
        if (options) {
          handleAudit?.();
        }
      } catch (err) {
        setErrorText(err.errorText || err.message);
      } finally {
        setIsDeviceAssociating(false);
      }
    } else {
      setFormState(errors);
    }
  }, [
    onSubmit,
    formState,
    handleAudit,
    removedDevice,
    onMoveDevice,
    setFormState,
    addedDevicesRef,
    isMultiSelectDevice,
    isEditMode,
    movingToGroup,
    isDeviceRemoved,
    includeAssetAssociation,
    isDeviceAssociated,
    defaultDevice
  ]);

  return {onSave, clearErrors, errorText, isDeviceAssociating};
};

export default useAssetSave;
